import { ColumnWidthOutlined, EnvironmentOutlined, FlagFilled, LinkOutlined } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { addCommasToNumber, BBDTag, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Button, Card, Input, Spin, Tooltip } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { List, AutoSizer, CellMeasurer, CellMeasurerCache } from 'react-virtualized'

export function PartnerStoreSearch({
  onStoreSelect,
  splitPartners,
  handleUpdateSplitPartner,
  searchValue,
  setSearchValue,
  fetchStatus,
  isCarrier,
  siteConfig,
}) {
  const [hovering, setHovering] = useState('')
  const { formatDistance } = useFormatter()
  const formattedDistance = formatDistance(null, false)

  const cache = useMemo(
    () =>
      new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: 55,
        minHeight: 35,
      }),
    []
  )

  const listItems = useMemo(() => {
    if (!splitPartners) return []
    let result = [...splitPartners]
    result = result?.filter(
      (site) =>
        site.store_number.toUpperCase().includes(searchValue?.toUpperCase()) ||
        site.name.toUpperCase().includes(searchValue?.toUpperCase())
    )
    if (!siteConfig.includeCarrierManaged && !isCarrier) {
      result = result?.filter((site) => !site.managers.length)
    }
    if (!result?.length) return []
    if (result[0].split_partner) {
      result.unshift({ label: 'SPLIT PARTNERS', split_partner: true })
    }
    const nearbyIndex = result.findIndex((site) => !site.split_partner)
    if (nearbyIndex !== -1) result.splice(nearbyIndex, 0, { label: 'NEARBY SITES' })
    return result
  }, [splitPartners, siteConfig?.includeCarrierManaged, searchValue])

  useEffect(() => {
    cache.clearAll()
  }, [searchValue, splitPartners, siteConfig?.includeCarrierManaged, isCarrier, siteConfig])

  const rowRenderer = ({ key, index, style, parent }) => {
    const site = listItems[index]

    return (
      <CellMeasurer cache={cache} columnIndex={0} key={key} rowIndex={index} parent={parent}>
        {({ measure }) => (
          <div style={style} onLoad={measure}>
            {site?.label ? (
              <Horizontal bg='elevated' border='all' className='p-2'>
                <Texto style={{ color: 'var(--gray-500)' }}>{site.label}</Texto>
              </Horizontal>
            ) : (
              <Horizontal
                verticalCenter
                justifyContent='space-between'
                className='border-bottom py-2 px-3 split-partners-select'
                onClick={() => onStoreSelect(site.store_number, site.id)}
              >
                <Vertical verticalCenter gap={8} flex='0' style={{ overflow: 'visible' }}>
                  {site.split_partner ? (
                    <Tooltip
                      placement='topRight'
                      onOpenChange={(visible) => (visible ? setHovering(site.id) : setHovering(''))}
                      trigger='hover'
                      title='Unpin As Split Partner'
                    >
                      <GraviButton
                        appearance='link'
                        icon={
                          hovering !== site.id ? (
                            <LinkOutlined style={{ color: 'var(--theme-color-2)', fontSize: '1.5em' }} />
                          ) : (
                            <EnvironmentOutlined style={{ color: 'var(--theme-color-2)', fontSize: '1.5em' }} />
                          )
                        }
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleUpdateSplitPartner(site, false, cache)
                          setHovering('')
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip
                      placement='topRight'
                      onOpenChange={(visible) => (visible ? setHovering(site.id) : setHovering(''))}
                      trigger='hover'
                      title='Pin As Split Partner'
                    >
                      <GraviButton
                        appearance='link'
                        icon={
                          hovering === site.id ? (
                            <LinkOutlined style={{ color: 'var(--theme-color-2)', fontSize: '1.5em' }} />
                          ) : (
                            <EnvironmentOutlined style={{ color: 'var(--theme-color-2)', fontSize: '1.5em' }} />
                          )
                        }
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleUpdateSplitPartner(site, true)
                          setHovering('')
                        }}
                      />
                    </Tooltip>
                  )}
                </Vertical>
                <Vertical flex={2}>
                  <Tooltip placement='topRight' title={`${site.name} (${site.store_number})`} trigger='hover'>
                    <div>
                      <Texto
                        className='text-truncate'
                        appearance='secondary'
                        style={{ textDecoration: 'underline' }}
                        weight='bold'
                      >
                        {site.name} ({site.store_number})
                      </Texto>
                    </div>
                  </Tooltip>
                  <Horizontal>
                    <Texto className='text-truncate' weight='bold'>
                      {site.city}, {site.state}
                    </Texto>
                    {site?.managers?.length > 0 && !isCarrier && (
                      <Tooltip trigger='hover' title={site.managers.map((m) => m).join(', ')}>
                        <div>
                          <Texto
                            align='right'
                            className='text-truncate'
                            style={{ color: 'var(--theme-color-4)', width: '100%' }}
                            weight='bold'
                          >
                            <FlagFilled className='ml-2' />
                          </Texto>
                        </div>
                      </Tooltip>
                    )}
                  </Horizontal>
                </Vertical>
                <Vertical verticalCenter horizontalCenter flex={1}>
                  <Texto className='text-truncate' weight='bold'>
                    <ColumnWidthOutlined className='mr-1' />
                    {site.distance && addCommasToNumber(site.distance, 1)} Est. {formattedDistance}
                  </Texto>
                </Vertical>
                <Vertical flex={1} alignItems='end' justifyContent='center'>
                  {site?.split_me && (
                    <BBDTag className='mr-0' theme2>
                      SPLIT ME
                    </BBDTag>
                  )}
                </Vertical>
              </Horizontal>
            )}
          </div>
        )}
      </CellMeasurer>
    )
  }

  if (!splitPartners || fetchStatus === 'fetching') {
    return (
      <Card className='loading-store-container'>
        <Spin size='large' />
      </Card>
    )
  }

  return (
    <Vertical flex={1}>
      <Input.Search
        placeholder='Search For Site'
        data-cy='site-search'
        onChange={(e) => {
          setSearchValue(e.target.value)
          cache.clearAll()
        }}
        value={searchValue}
      />
      <Vertical flex={1}>
        <AutoSizer>
          {({ width, height }) => (
            <List
              width={width}
              height={height}
              deferredMeasurementCache={cache}
              rowHeight={cache.rowHeight}
              rowCount={listItems.length}
              rowRenderer={rowRenderer}
              overscanRowCount={5}
            />
          )}
        </AutoSizer>
      </Vertical>
    </Vertical>
  )
}
